<template lang="pug">
  section(v-if="this.hasAsessments")
    hr
    div(v-if="dassData")
      h2.heading DASS21 Assessment Scores
      line-chart(
        :chart-data="dassData"
        :options="getChartOptions(maxDassScore)"
        :css-classes="'chart-wrapper'"
        :width="400"
        :height="320"
      )
      .info
        p *Higher scores indicate higher severity
      h4.center-heading DASS21 Scoring
      table
        thead
          tr
            th Rating
            th Depression
            th Anxiety
        tbody
          tr
            td Normal
            td 0-9
            td 0-7
          tr
            td Mild
            td 10-13
            td 8-9
          tr
            td Moderate
            td 14-20
            td 10-14
          tr
            td Severe
            td 21-27
            td 15-19
          tr
            td Extremely Severe
            td 28+
            td 20+
    div(v-if="phqData")
      h2.heading PHQ-9 Assessment Scores
      line-chart(
        :chart-data="phqData"
        :options="getChartOptions(maxPhqScore)"
        :css-classes="'chart-wrapper'"
        :width="400"
        :height="320"
      )
      .info
        p *Higher scores indicate higher severity
      h4.center-heading PHQ-9 Scoring
      table
        thead
          tr
            th.small Score
            th Range
            th Treatment ?
        tbody
          tr
            td 0-4
            td Normal
            td
          tr
            td 5-9
            td Mild
            td
          tr
            td 10-14
            td Mild-Moderate
            td
          tr
            td 15-19
            td Moderate
            td
          tr
            td 20+
            td Severe
            td
    div(v-if="gadData")
      h2.heading GAD-7 Assessment Scores
      line-chart(
        :chart-data="gadData"
        :options="getChartOptions(maxGadScore)"
        :css-classes="'chart-wrapper'"
        :width="400"
        :height="320"
      )
      .info
        p *Higher scores indicate higher severity
      h4.center-heading GAD-7 Scoring
      table
        thead
          tr
            th.small Score
            th Range
        tbody
          tr
            td 0-4
            td Minimal Anxiety
          tr
            td 5-9
            td Mild Anxiety
          tr
            td 10-14
            td Moderate Anxiety
          tr
            td 15-21
            td Severe Anxiety
    div(v-if="who5Data")
      h2.heading WHO-5 Assessment Scores
      line-chart(
        :chart-data="who5Data"
        :options="getChartOptions(maxWho5Score)"
        :css-classes="'chart-wrapper'"
        :width="400"
        :height="320"
      )
      .info
        p *Lower scores indicate higher severity
      h4.center-heading WHO-5 Scoring
      table
        thead
          tr
            th.small Score
            th Range
        tbody
          tr
            td 0-12
            td Severe
          tr
            td 13-24
            td Moderate
          tr
            td 25
            td Normal
  section(v-else)
    h2.heading Assessment Scores
    p No data captured
</template>

<script>
import LineChart from '@/components/charts/LineChart.js';
import chartMixin from '@/mixins/chartData.js';
import _ from 'lodash';

export default {
  name: 'PatientAssessment',
  components: {
    LineChart
  },
  props: [ 'assessments' ],
  mixins: [ chartMixin ],
  computed: {
    hasAsessments() {
      return Array.isArray(this.assessments) && this.assessments.length > 0;
    },
    dassData() {
      const filtered = _.filter(this.assessments, (a) => a.assessmentName.toLowerCase() === 'dass21');
      if (filtered.length === 0) {
        return null;
      }

      this.dassChart.labels = this.getFormattedDates(_.map(filtered, 'modifiedOn'));
      filtered.forEach((item, index) => {
        if (item.categoryName === 'Depression') {
          this.dassChart.datasets[0].data.push(item.score);
        } else if (item.categoryName === 'Anxiety') {
          this.dassChart.datasets[1].data.push(item.score);
        }
      });
      return this.dassChart;
    },
    phqData() {
      const filtered = _.filter(this.assessments, (a) => a.assessmentName.toLowerCase() === 'phq9');
      if (filtered.length === 0) {
        return null;
      }

      this.phqChart.labels = this.getFormattedDates(_.map(filtered, 'modifiedOn'));
      this.phqChart.datasets[0].data = _.map(filtered, 'score');
      return this.phqChart;
    },
    gadData() {
      const filtered = _.filter(this.assessments, (a) => a.assessmentName.toLowerCase() === 'gad7');
      if (filtered.length === 0) {
        return null;
      }

      this.gadChart.labels = this.getFormattedDates(_.map(filtered, 'modifiedOn'));
      this.gadChart.datasets[0].data = _.map(filtered, 'score');
      return this.gadChart;
    },
    who5Data() {
      const filtered = _.filter(this.assessments, (a) => a.assessmentName.toLowerCase() === 'who-5');
      if (filtered.length === 0) {
        return null;
      }

      this.who5Chart.labels = this.getFormattedDates(_.map(filtered, 'modifiedOn'));
      this.who5Chart.datasets[0].data = _.map(filtered, 'score');
      return this.who5Chart;
    }
  },
  methods: {
    getChartOptions(maxScore) {
      let options = Object.assign({}, this.baseChartOptions);
      options.scales.yAxes[0].ticks.suggestedMax = maxScore;
      return options;
    }
  },
  data() {
    return {
      maxDassScore: 42,
      maxPhqScore: 27,
      maxGadScore: 21,
      maxWho5Score: 25,
      baseChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              suggestedMin: 0,
              suggestedMax: 100
            }
          }]
        },
        layout: {
          padding: {
            left: 8,
            right: 16
          }
        }
      },
      dassChart: {
        labels: [],
        datasets: [
          {
            label: 'DASS21 Depression',
            lineTension: 0.25,
            backgroundColor: 'rgba(0,124,166,0.1)',
            borderColor: 'rgba(0,124,166,0.9)',
            borderWidth: '2',
            pointBackgroundColor: '#fff',
            pointRadius: 4,
            data: []
          },
          {
            label: 'DASS21 Anxiety',
            lineTension: 0.25,
            backgroundColor: 'rgba(56,142,60,0.1)',
            borderColor: 'rgba(56,142,60,0.9)',
            borderWidth: '2',
            pointBackgroundColor: '#fff',
            pointRadius: 4,
            data: []
          }
        ]
      },
      phqChart: {
        labels: [],
        datasets: [
          {
            label: 'PHQ-9',
            lineTension: 0.15,
            backgroundColor: 'rgba(250,160,0,0.2)',
            borderColor: 'rgba(250,160,0,0.9)',
            borderWidth: '2',
            pointBackgroundColor: '#fff',
            pointRadius: 4,
            data: []
          }
        ]
      },
      gadChart: {
        labels: [],
        datasets: [
          {
            label: 'GAD-7',
            lineTension: 0,
            backgroundColor: 'rgba(125,93,150,0.2)',
            borderColor: 'rgba(125,93,150,0.9)',
            borderWidth: '2',
            pointBackgroundColor: '#fff',
            pointRadius: 4,
            data: []
          }
        ]
      },
      who5Chart: {
        labels: [],
        datasets: [
          {
            label: 'WHO-5',
            lineTension: 0,
            backgroundColor: 'rgba(200,175,50,0.2)',
            borderColor: 'rgba(200,175,50,0.9)',
            borderWidth: '2',
            pointBackgroundColor: '#fff',
            pointRadius: 4,
            data: []
          }
        ]
      }
    };
  }
};
</script>

<style lang="scss" scoped>

  .info {
    position: relative;
    margin: -$base-spacing auto $base-spacing;
    padding: $small-spacing;
    background: $md-grey-100;
    border-bottom-left-radius: 1em;
    border-bottom-right-radius: 1em;
    p {
      @include zero;
      font-family: $serif-font-family;
      font-style: italic;
      font-size: $ms-down1;
      line-height: 1;
    }
  }

  table {
    font-size: $ms-down1;
    margin: -$small-spacing auto $base-spacing*2;
    padding: $small-spacing;
    .small {
      width: 6em;
    }
  }
</style>
